import React, {useState, useEffect, useRef} from 'react';
import {db} from '../../firebase';
import {doc, addDoc, updateDoc, collection, serverTimestamp} from 'firebase/firestore';
import {FiMoreVertical} from 'react-icons/fi'; // Icon for the menu button
import '../../styles/CasefileForm.css';

// Predefined list of categories as requested
const PREDEFINED_CATEGORIES = [
  'Assault/Battery',
  'Recklessness',
  'Theft',
  'Property Damage',
  'Vehicle',
  'Other',
];

const CasefileForm = ({user, onClose, existingCasefile, onUpdateSuccess}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);

  // --- NEW STATE FOR CATEGORIES ---
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const categoryMenuRef = useRef(null);

  // Pre-fill form if editing
  useEffect(() => {
    if (existingCasefile) {
      setTitle(existingCasefile.title || '');
      setDescription(existingCasefile.description || '');
      setSelectedCategories(existingCasefile.categories || []);
    }
  }, [existingCasefile]);

  // --- Close dropdown if clicking outside ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryMenuRef.current && !categoryMenuRef.current.contains(event.target)) {
        setIsCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Enhanced validation
    if (!title || title.trim().length === 0) {
      setMessage('Title is required and cannot be empty.');
      return;
    }
    
    if (title.trim().length < 3) {
      setMessage('Title must be at least 3 characters long.');
      return;
    }
    
    if (title.trim().length > 100) {
      setMessage('Title must be less than 100 characters long.');
      return;
    }
    
    if (!description || description.trim().length === 0) {
      setMessage('Description is required and cannot be empty.');
      return;
    }
    
    if (description.trim().length < 10) {
      setMessage('Description must be at least 10 characters long.');
      return;
    }
    
    if (description.trim().length > 2000) {
      setMessage('Description must be less than 2000 characters long.');
      return;
    }
    
    setIsConfirming(true);
  };

  const handleConfirmSave = async () => {
    if (!user) {
      setMessage('You must be logged in to perform this action.');
      setIsConfirming(false);
      return;
    }
    setIsLoading(true);
    setMessage('');

    try {
      if (existingCasefile) {
        // --- UPDATE operation ---
        const casefileRef = doc(db, 'casefiles', existingCasefile.id);
        const updatedData = {
          title: title,
          description: description,
          updatedAt: serverTimestamp(),
          categories: selectedCategories,
        };
        await updateDoc(casefileRef, updatedData);
        if (onUpdateSuccess) {
          onUpdateSuccess({...existingCasefile, ...updatedData});
        }
      } else {
        // --- CREATE operation ---
        await addDoc(collection(db, 'casefiles'), {
          title: title,
          description: description,
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          creatorEmail: user.email,
          isPublic: false,
          categories: selectedCategories,
        });
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving document: ', error);
      setMessage('Failed to save changes. Please try again.');
    } finally {
      setIsLoading(false);
      setIsConfirming(false);
    }
  };

  const handleCancelConfirm = () => {
    setIsConfirming(false);
  };

  // --- Function to handle selecting/deselecting a category ---
  const handleCategoryToggle = (category) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  // --- Filter categories based on search term ---
  const filteredCategories = PREDEFINED_CATEGORIES.filter((category) =>
    category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      {isConfirming ? (
        <div className="confirmation-dialog">
          <h4>Confirm Changes</h4>
          <p>Are you sure you want to save these changes?</p>
          <div className="confirmation-actions">
            <button onClick={handleCancelConfirm} className="btn-cancel" disabled={isLoading}>
              Cancel
            </button>
            <button onClick={handleConfirmSave} className="btn-confirm-save" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Confirm & Save'}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <h3 style={{marginTop: 0, marginBottom: '25px'}}>
            {existingCasefile ? 'Edit Casefile Details' : 'Create a New Casefile'}
          </h3>

          <div style={{marginBottom: '15px'}}>
            <label htmlFor="title" style={{display: 'block', marginBottom: '5px'}}>
              Casefile Title
              <span style={{color: '#888', fontSize: '0.8em', marginLeft: '10px'}}>
                ({title.length}/100 characters)
              </span>
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              required
              style={{
                width: '100%', 
                padding: '10px', 
                background: '#333', 
                border: title.length > 100 ? '1px solid #ff4d4d' : '1px solid #555', 
                color: '#fff', 
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{marginBottom: '20px'}}>
            <label htmlFor="description" style={{display: 'block', marginBottom: '5px'}}>
              Casefile Description
              <span style={{color: '#888', fontSize: '0.8em', marginLeft: '10px'}}>
                ({description.length}/2000 characters)
              </span>
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              required
              style={{
                width: '100%', 
                padding: '10px', 
                minHeight: '180px', 
                background: '#333', 
                border: description.length > 2000 ? '1px solid #ff4d4d' : '1px solid #555', 
                color: '#fff', 
                borderRadius: '4px'
              }}
            />
          </div>

          {/* --- CATEGORY SELECTION UI --- */}
          <div className="category-section" ref={categoryMenuRef}>
            <div className="category-header">
              <label>Categories</label>
              <button type="button" className="category-menu-button" onClick={() => setIsCategoryOpen(!isCategoryOpen)}>
                <FiMoreVertical />
              </button>
            </div>
            {selectedCategories.length > 0 && (
                <div className="selected-tags-container">
                    {selectedCategories.map((category) => (
                        <span key={category} className="tag-pill">{category}</span>
                    ))}
                </div>
            )}
            {isCategoryOpen && (
              <div className="category-dropdown">
                <input
                  type="text"
                  placeholder="Search for flair"
                  className="category-search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <ul className="category-list">
                  {filteredCategories.map((category) => (
                    <li
                      key={category}
                      className={`category-item ${selectedCategories.includes(category) ? 'selected' : ''}`}
                      onClick={() => handleCategoryToggle(category)}
                    >
                      <span className="selection-circle"></span>
                      {category}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="draft-btn-container">
            <button type="submit" disabled={isLoading} className="draft-btn">
              {existingCasefile ? 'Save Changes' : 'Draft'}
            </button>
          </div>
          {message && <p style={{marginTop: '15px', color: '#ff4d4d', textAlign: 'center'}}>{message}</p>}
        </form>
      )}
    </>
  );
};

export default CasefileForm; 